import type { Terminal } from '@xterm/xterm';

export interface MenuItem {
  digit: string;
  label: string;
  selected: boolean;
}

/** Supported terminal menus render choices as a numbered list, marking the current one with ❯.
 *  Matches both the marked and unmarked rows. */
const ROW = /^\s*(❯|›|>)?\s*(\d)[.)]\s+(.{1,80}?)\s*$/;
/** How far up from the cursor to look. Menus sit just above the prompt. */
const LOOKBACK = 18;

/**
 * Find a numbered menu on screen, so it can be offered as real buttons.
 *
 * This reads rendered text, which makes it the most fragile thing in the app: any
 * restyling of Codex's menus breaks it. It is built to fail quietly — no match
 * means no buttons, and the arrow keys and Enter keep working exactly as before.
 * Nothing depends on this succeeding.
 */
export function readMenu(term: Terminal): MenuItem[] {
  const buf = term.buffer.active;
  const bottom = buf.baseY + buf.cursorY;
  const items: MenuItem[] = [];

  for (let y = Math.max(0, bottom - LOOKBACK); y <= bottom; y++) {
    const line = buf.getLine(y);
    if (!line) continue;
    const m = ROW.exec(line.translateToString(true));
    if (!m) continue;

    const [, marker, digit, label] = m;
    if (!digit || !label) continue;
    // Sequential digits only: `1.` then `2.` is a menu, whereas a stray "3. foo" in
    // prose is not. This is what keeps ordinary output from sprouting buttons.
    if (items.length && Number(digit) !== Number(items[items.length - 1]!.digit) + 1) {
      items.length = 0;
    }
    if (!items.length && digit !== '1') continue;
    items.push({ digit, label: label.trim(), selected: Boolean(marker) });
  }

  // Two rules, and the second is the one that matters.
  //
  // A single "1." is more likely prose than a menu — hence >= 2. But a numbered list
  // *inside an answer* ("1. Первое / 2. Второе") is textually identical to a menu, and
  // offering buttons for it would type a stray digit into the prompt. What separates
  // them is the ❯ marker: A menu marks the highlighted row of a real menu, and
  // prose never has one. Requiring it trades a rare miss for no false positives, which
  // is the right way round for something that injects keystrokes.
  return items.length >= 2 && items.some((i) => i.selected) ? items : [];
}
